import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Icon } from "@/lib/icons";
import { Txt } from "@/components/Txt";
import { Button, Input, Toggle } from "@/components/kit";

export default function DuesSettings() {
  const [cfg, setCfg] = useState(null);
  const [types, setTypes] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [newReason, setNewReason] = useState("");
  const [graceDays, setGraceDays] = useState("");
  const [celebrate, setCelebrate] = useState("");
  const [promo, setPromo] = useState({ enabled: false, start_date: "", end_date: "", note: "" });

  const load = useCallback(async () => {
    const r = await api.get("/membership/dues-config");
    setCfg(r.data.dues);
    setTypes(r.data.account_types);
    setReasons(r.data.waived_reasons);
    try { const g = await api.get("/membership/gate-config"); setGraceDays(String(g.data.grace_days ?? 14)); } catch (e) {}
    try { const c = await api.get("/membership/config"); setCelebrate(c.data.celebration_message || ""); } catch (e) {}
    try { const p = await api.get("/membership/promo-config"); const v = p.data.promo || {}; setPromo({ enabled: !!v.enabled, start_date: v.start_date || "", end_date: v.end_date || "", note: v.note || "" }); } catch (e) {}
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!cfg) return <div className="text-ink/60">Loading…</div>;

  const setAmount = (tid, field, dollars) => {
    const cents = Math.round(parseFloat(dollars || "0") * 100);
    setCfg((c) => ({ ...c, types: { ...c.types, [tid]: { ...c.types[tid], [field]: cents } } }));
  };
  const saveDues = async () => {
    try { await api.put("/membership/dues-config", cfg); toast.success("Dues settings saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  const saveGrace = async () => {
    const days = Math.max(0, parseInt(graceDays || "0", 10));
    try { const r = await api.put("/membership/gate-config", { grace_days: days }); setGraceDays(String(r.data.grace_days)); toast.success("Grace period saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  const saveReasons = async (list) => {
    try { const r = await api.put("/membership/waived-reasons", { reasons: list }); setReasons(r.data.reasons); toast.success("Reasons updated"); }
    catch (e) { toast.error("Could not save reasons"); }
  };
  const saveCelebrate = async () => {
    try { const r = await api.put("/membership/celebration-message", { message: celebrate }); setCelebrate(r.data.message); toast.success("Welcome message saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  const savePromo = async () => {
    try {
      const r = await api.put("/membership/promo-config", promo);
      const v = r.data.promo || {};
      setPromo({ enabled: !!v.enabled, start_date: v.start_date || "", end_date: v.end_date || "", note: v.note || "" });
      toast.success(r.data.active ? "Promotional Free Membership is ON — new signups are free" : "Promotion saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };

  return (
    <div className="max-w-2xl" data-testid="dues-settings-page">
      <h1 className="font-zilla text-3xl sm:text-4xl font-semibold mb-1"><Txt id="membership.dues.title" screen="Membership · Dues">Dues &amp; billing</Txt></h1>
      <p className="text-[16px] text-ink/70 mb-5"><Txt id="membership.dues.subtitle" screen="Membership · Dues">Set dues per account type. Turning a billing option off removes it from every paid type on Join Rosemoor.</Txt></p>

      <div className="bg-cream border border-ink/15 rounded-xl p-5 mb-5">
        <div className="flex items-center justify-between py-2">
          <span className="text-[15px] font-semibold">Monthly billing</span>
          <Toggle data-testid="toggle-monthly" checked={cfg.monthly_enabled} onChange={(v) => setCfg({ ...cfg, monthly_enabled: v })} />
        </div>
        <div className="flex items-center justify-between py-2 border-t border-ink/10">
          <span className="text-[15px] font-semibold">Yearly billing</span>
          <Toggle data-testid="toggle-yearly" checked={cfg.yearly_enabled} onChange={(v) => setCfg({ ...cfg, yearly_enabled: v })} />
        </div>
      </div>

      <div className="bg-cream border border-ink/15 rounded-xl p-5 mb-3" data-testid="dues-amounts">
        <h2 className="font-zilla text-xl font-semibold mb-3">Amounts per type</h2>
        {types.map((t) => (
          <div key={t.id} className="grid grid-cols-3 gap-3 items-center py-2 border-b border-ink/8 last:border-0">
            <span className="text-[15px] font-semibold">{t.label}</span>
            <label className="text-[13px] text-ink/60">Monthly $
              <Input data-testid={`dues-${t.id}-monthly`} type="number" step="0.01" value={((cfg.types[t.id]?.monthly_cents || 0) / 100).toString()} onChange={(e) => setAmount(t.id, "monthly_cents", e.target.value)} disabled={!cfg.monthly_enabled} className="block min-h-[40px] py-2 mt-1 disabled:opacity-40" />
            </label>
            <label className="text-[13px] text-ink/60">Yearly $
              <Input data-testid={`dues-${t.id}-yearly`} type="number" step="0.01" value={((cfg.types[t.id]?.yearly_cents || 0) / 100).toString()} onChange={(e) => setAmount(t.id, "yearly_cents", e.target.value)} disabled={!cfg.yearly_enabled} className="block min-h-[40px] py-2 mt-1 disabled:opacity-40" />
            </label>
          </div>
        ))}
      </div>
      <Button data-testid="save-dues" onClick={saveDues} className="px-6 py-3 min-h-[48px] mb-8"><Txt id="membership.dues.save" screen="Membership · Dues">Save dues settings</Txt></Button>

      <div className="bg-cream border border-ink/15 rounded-xl p-5 mb-8" data-testid="gate-config">
        <h2 className="font-zilla text-xl font-semibold mb-1">Sign-in gate grace period</h2>
        <p className="text-[13px] text-ink/55 mb-3">Members with lapsed or unpaid dues are blocked at sign-in until they pay. They get this many days to resolve it before the account is automatically suspended. Set 0 to suspend immediately.</p>
        <div className="flex items-end gap-3">
          <label className="text-[13px] text-ink/60">Grace period (days)
            <Input data-testid="grace-days-input" type="number" min="0" value={graceDays} onChange={(e) => setGraceDays(e.target.value)} className="block w-32 mt-1" />
          </label>
          <Button data-testid="save-grace" onClick={saveGrace} className="px-5">Save</Button>
        </div>
      </div>

      <div className="bg-cream border border-ink/15 rounded-xl p-5 mb-8" data-testid="promo-free-config">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-zilla text-xl font-semibold">Promotional Free Membership</h2>
          <Toggle data-testid="toggle-promo-free" checked={promo.enabled} onChange={(v) => setPromo({ ...promo, enabled: v })} />
        </div>
        <p className="text-[13px] text-ink/55 mb-3">While this is on, every new signup during the window below has dues waived automatically, the reason shows on the Join screen, and no member sees a dues countdown or expiration warning at sign-in.</p>
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <label className="text-[13px] text-ink/60">Start date
            <Input data-testid="promo-start-date" type="date" value={promo.start_date} onChange={(e) => setPromo({ ...promo, start_date: e.target.value })} className="block w-full mt-1" />
          </label>
          <label className="text-[13px] text-ink/60">End date
            <Input data-testid="promo-end-date" type="date" value={promo.end_date} onChange={(e) => setPromo({ ...promo, end_date: e.target.value })} className="block w-full mt-1" />
          </label>
        </div>
        <label className="text-[13px] text-ink/60 block mb-3">Reason shown on the Join screen
          <Input data-testid="promo-note" value={promo.note} onChange={(e) => setPromo({ ...promo, note: e.target.value })} placeholder="e.g. Dues are on us right now — welcome to Rosemoor!" className="block w-full mt-1" />
        </label>
        <Button data-testid="save-promo" onClick={savePromo} className="px-5">Save promotion</Button>
      </div>

      <div className="bg-cream border border-ink/15 rounded-xl p-5 mb-8" data-testid="celebration-editor">
        <h2 className="font-zilla text-xl font-semibold mb-1">New-member welcome message</h2>
        <p className="text-[13px] text-ink/55 mb-3">Shown with the rose-petal celebration on the confirmation screen right after someone joins and their payment clears.</p>
        <div className="flex items-end gap-3 flex-wrap">
          <label className="text-[13px] text-ink/60 flex-1 min-w-[240px]">Message
            <Input data-testid="celebration-input" value={celebrate} onChange={(e) => setCelebrate(e.target.value)} placeholder="Welcome! You're a member!" className="block w-full mt-1" />
          </label>
          <Button data-testid="save-celebration" onClick={saveCelebrate} className="px-5">Save</Button>
        </div>
      </div>

      <div className="bg-cream border border-ink/15 rounded-xl p-5" data-testid="waived-reasons-editor">
        <h2 className="font-zilla text-xl font-semibold mb-2">Waived reasons</h2>
        <p className="text-[13px] text-ink/55 mb-3">Shared by waived status on records and by waiver codes.</p>
        <div className="flex flex-wrap gap-2 mb-3">
          {reasons.map((r) => (
            <span key={r} className="text-[14px] px-3 py-1.5 rounded-full bg-olive/20 border border-olive/40 flex items-center gap-1.5">
              {r}
              <button data-testid={`remove-reason-${r}`} onClick={() => saveReasons(reasons.filter((x) => x !== r))} className="text-maroon"><Icon name="X" className="w-3.5 h-3.5" /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <Input data-testid="new-reason" placeholder="Add a reason" value={newReason} onChange={(e) => setNewReason(e.target.value)} className="flex-1" />
          <Button data-testid="add-reason" onClick={() => { if (newReason.trim()) { saveReasons([...reasons, newReason.trim()]); setNewReason(""); } }} className="px-4">Add</Button>
        </div>
      </div>
    </div>
  );
}
